import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Mail, MessageSquare, Send, Loader2, Eye, Pencil, Users } from 'lucide-react';

// Shared "Send / Resend" dialog for Estimates and Invoices. Works regardless
// of the document's current status (draft, sent, viewed, etc). Lets the user
// review/edit the recipient's email + phone at send time, and choose to send
// by Email, SMS, or both. Calling POST /{docType}s/{id}/send is idempotent on
// resend — it only flips status/public_token on the very first send.
export function SendDocumentDialog({ open, onClose, docType, doc, customer, onSent }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [sendSms, setSendSms] = useState(true);
  const [updateContact, setUpdateContact] = useState(false);
  const [footerOverride, setFooterOverride] = useState('');
  const [showFooter, setShowFooter] = useState(false);
  const [emailStatus, setEmailStatus] = useState({ configured: false, test_mode: true });
  const [smsStatus, setSmsStatus] = useState({ configured: false, test_mode: true });
  const [sending, setSending] = useState(false);
  const [showMessageEditor, setShowMessageEditor] = useState(false);
  const [subjectDraft, setSubjectDraft] = useState('');
  const [bodyDraft, setBodyDraft] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [selectedPersonId, setSelectedPersonId] = useState('primary');
  // Fix F (2026-08-31) bug fix (testing_agent iteration 200, CRITICAL): people
  // seeded/imported outside this dialog's own "+ Add Person" button have no
  // `id` (only the frontend helper stamps crypto.randomUUID()) — that made
  // every SelectItem's value/key undefined and the picker a no-op. Derive a
  // stable fallback id here (index-based) so ANY contact_people entry works,
  // regardless of how it was created. Also drop any entry that's just a
  // duplicate of the contact's own primary email/phone.
  const additionalPeople = ((customer?.account_type === 'commercial' && customer?.contact_people) || [])
    .map((p, i) => ({ ...p, id: p.id || `p-${i}` }))
    .filter(p => {
      const sameEmail = p.email && customer?.email && p.email.toLowerCase() === customer.email.toLowerCase();
      const samePhone = p.phone && customer?.phone && p.phone === customer.phone;
      return !sameEmail && !samePhone;
    });

  const handlePersonSelect = (personId) => {
    setSelectedPersonId(personId);
    if (personId === 'primary') {
      setEmail(customer?.email || '');
      setPhone(customer?.phone || '');
      return;
    }
    const p = additionalPeople.find(x => x.id === personId);
    if (p) {
      setEmail(p.email || '');
      setPhone(p.phone || '');
    }
  };

  useEffect(() => {
    if (!open) return;
    setSelectedPersonId('primary');
    setEmail(customer?.email || '');
    setPhone(customer?.phone || '');
    setSendEmail(true);
    setSendSms(true);
    setUpdateContact(false);
    setShowFooter(false);
    setShowMessageEditor(false);
    setPreview(null);
    Promise.all([
      api.get('/emails/status').catch(() => ({ data: { configured: false } })),
      api.get('/voice/sms/status').catch(() => ({ data: { configured: false } })),
      api.get('/settings/email-templates').catch(() => ({ data: {} })),
    ]).then(([em, sm, tpl]) => {
      setEmailStatus(em.data || { configured: false });
      setSmsStatus(sm.data || { configured: false });
      setFooterOverride(em.data?.email_footer || '');
      setSubjectDraft(tpl.data?.[`${docType}_subject`] || '');
      setBodyDraft(tpl.data?.[`${docType}_body`] || '');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, doc?.id]);

  const isResend = doc && doc.status && doc.status !== 'draft';
  const docNumber = docType === 'estimate' ? doc?.estimate_number : doc?.invoice_number;

  const loadPreview = async () => {
    if (!doc?.id) return;
    setPreviewLoading(true);
    try {
      const res = await api.post(`/emails/preview-${docType}/${doc.id}`, {
        subject_override: subjectDraft, body_override: bodyDraft,
      });
      setPreview(res.data);
    } catch {
      toast.error(t('sendDialog.previewError', { defaultValue: 'Could not load preview.' }));
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!doc?.id) return;
    if (!sendEmail && !sendSms) {
      toast.error(t('sendDialog.selectChannel', { defaultValue: 'Select Email and/or SMS to continue.' }));
      return;
    }
    setSending(true);
    try {
      const res = await api.post(`/${docType}s/${doc.id}/send`, {
        email: email || null,
        phone: phone || null,
        send_email: sendEmail,
        send_sms: sendSms,
        update_contact: updateContact,
        footer_override: footerOverride || null,
        ...(showMessageEditor ? { subject_override: subjectDraft, body_override: bodyDraft } : {}),
      });
      const em = res.data?.email;
      const sm = res.data?.sms;
      const parts = [];
      if (sendEmail) parts.push(em?.sent ? `Email → ${(em.to || []).join(', ') || 'sent'}` : `Email failed (${em?.reason || 'unknown'})`);
      if (sendSms) parts.push(sm?.sent ? `SMS → ${sm.to || 'sent'}` : `SMS failed (${sm?.reason || 'unknown'})`);
      if (parts.some((p) => p.includes('failed'))) toast.warning(parts.join(' · '));
      else toast.success(parts.length ? parts.join(' · ') : t('common.success', { defaultValue: 'Sent' }));
      onSent && onSent(res.data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('errors.savingError'));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="send-document-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Barlow']">
            <span className="flex items-center gap-2">
              <Send className="w-5 h-5 text-emerald-600" />
              {isResend ? 'Resend' : 'Send'} {docType === 'estimate' ? 'Estimate' : 'Invoice'} — {docNumber}
            </span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {(emailStatus.test_mode || smsStatus.test_mode) && (
            <div className="text-xs text-amber-800 bg-amber-100 border-2 border-amber-400 rounded-md px-3 py-2.5 font-semibold flex items-start gap-2" data-testid="send-dialog-test-mode-banner">
              <span className="text-base leading-none">⚠</span>
              <span>
                TEST MODE ON — this will NOT reach the customer.
                {emailStatus.test_mode && emailStatus.configured && (
                  <> Email goes to <strong>{emailStatus.test_to || 'the test inbox'}</strong> instead of {email || 'the customer'}.</>
                )}
                {smsStatus.test_mode && smsStatus.configured && (
                  <> SMS goes to the test number instead of {phone || 'the customer'}.</>
                )}
              </span>
            </div>
          )}

          {additionalPeople.length > 0 && (
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-600 flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> Send to</Label>
              <Select value={selectedPersonId} onValueChange={handlePersonSelect}>
                <SelectTrigger data-testid="send-dialog-recipient-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="primary">
                    {[customer?.first_name, customer?.last_name].filter(Boolean).join(' ') || 'Primary Contact'}{customer?.company_name ? ` (${customer.company_name})` : ''}
                  </SelectItem>
                  {additionalPeople.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {[p.first_name, p.last_name].filter(Boolean).join(' ') || 'Contact'}{customer?.company_name ? ` (${customer.company_name})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-1.5">
            <Label className="text-xs text-gray-600 flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> Email</Label>
            <Input
              data-testid="send-dialog-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="customer@email.com"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-gray-600 flex items-center gap-1.5"><MessageSquare className="w-3.5 h-3.5" /> Phone</Label>
            <Input
              data-testid="send-dialog-phone-input"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+1 (555) 123-4567"
            />
          </div>

          <div className="flex items-center gap-6 pt-1">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox data-testid="send-dialog-email-checkbox" checked={sendEmail} onCheckedChange={(v) => setSendEmail(!!v)} disabled={!emailStatus.configured} />
              Send by Email{!emailStatus.configured && <span className="text-[10px] text-gray-400">(not configured)</span>}
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox data-testid="send-dialog-sms-checkbox" checked={sendSms} onCheckedChange={(v) => setSendSms(!!v)} disabled={!smsStatus.configured} />
              Send by SMS{!smsStatus.configured && <span className="text-[10px] text-gray-400">(not configured)</span>}
            </label>
          </div>

          <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer">
            <Checkbox data-testid="send-dialog-update-contact-checkbox" checked={updateContact} onCheckedChange={(v) => setUpdateContact(!!v)} />
            Also update this customer's saved email/phone with these values
          </label>

          <button
            type="button"
            data-testid="send-dialog-toggle-footer"
            className="text-[11px] text-blue-500 hover:underline"
            onClick={() => setShowFooter((v) => !v)}
          >
            {showFooter ? 'Hide' : 'Edit'} email footer (optional)
          </button>
          {showFooter && (
            <Textarea
              data-testid="send-dialog-footer-input"
              value={footerOverride}
              onChange={(e) => setFooterOverride(e.target.value)}
              rows={4}
              placeholder="Footer text..."
            />
          )}

          <button
            type="button"
            data-testid="send-dialog-toggle-message-editor"
            className="text-[11px] text-blue-500 hover:underline flex items-center gap-1"
            onClick={() => setShowMessageEditor((v) => !v)}
          >
            <Pencil className="w-3 h-3" /> {showMessageEditor ? 'Hide' : 'Edit / Preview'} email message
          </button>
          {showMessageEditor && (
            <div className="space-y-3 p-3 rounded-lg border border-gray-200 bg-gray-50/60">
              <div className="space-y-1">
                <Label className="text-[11px] text-gray-500">Subject</Label>
                <Input
                  data-testid="send-dialog-subject-input"
                  value={subjectDraft}
                  onChange={(e) => setSubjectDraft(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-[11px] text-gray-500">Message body</Label>
                <Textarea
                  data-testid="send-dialog-body-input"
                  value={bodyDraft}
                  onChange={(e) => setBodyDraft(e.target.value)}
                  rows={6}
                  className="font-mono text-xs"
                />
                <p className="text-[10px] text-gray-400">
                  Variables: {docType === 'invoice'
                    ? '{customer_name} {invoice_number} {total} {amount_due} {due_date} {company_phone} {pay_link} {items_table}'
                    : '{customer_name} {estimate_number} {total} {company_phone} {approve_link} {items_table}'}
                  &nbsp;— this only changes THIS send, the saved template in Settings stays the same.
                </p>
              </div>
              <Button type="button" size="sm" variant="outline" onClick={loadPreview} disabled={previewLoading} data-testid="send-dialog-preview-btn">
                {previewLoading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Eye className="w-3.5 h-3.5 mr-1.5" />}
                Preview as customer will see it
              </Button>
              {preview && (
                <div className="border border-gray-200 rounded-lg overflow-hidden bg-white" data-testid="send-dialog-preview-pane">
                  <div className="px-3 py-2 bg-gray-100 border-b border-gray-200">
                    <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Subject</p>
                    <p className="text-sm font-medium text-gray-800">{preview.subject}</p>
                  </div>
                  <div className="p-3 max-h-64 overflow-y-auto text-sm" dangerouslySetInnerHTML={{ __html: preview.html }} />
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={sending}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            data-testid="send-dialog-confirm-btn"
            onClick={handleSubmit}
            disabled={sending}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            {sending ? t('common.loading') : (isResend ? 'Resend' : 'Send')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default SendDocumentDialog;
